/* eslint-disable react-hooks/exhaustive-deps */
import {
  COLORS,
  LBTButtonFilter,
  LBTProgressSpinner,
} from '@laborability/components';
import 'chart.js/auto';
import {
  COMPARISION_PERIOD,
  DateAggregationEnum,
  PERIOD,
  TABS,
  VIEWS,
  viewTypeEnum,
} from './types';
import { useEffect, useState } from 'react';
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  AnalyticMetricRecord,
  currentUserState,
  getAllCompaniesCallback,
  getMetricCallback,
  getUserProfileCallback,
  Metric,
  MetricClass,
  UserRole,
} from '@laborability/commons';
import { appBarState } from '../../recoil/states/appBar';
import { Filter } from './Filter';
import Chart from './Chart';
import dayjs, { Dayjs } from 'dayjs';
import Panel from './Panel';
import Table from './Table';

export const Home = () => {
  const getAllCompanies = useRecoilCallback(getAllCompaniesCallback, []);
  const getUserMe = useRecoilCallback(getUserProfileCallback, []);
  const setAppBarName = useSetRecoilState(appBarState);
  const currentUser = useRecoilValue(currentUserState);

  const [tabId, setTabId] = useState<number>(1);
  const currentTab = TABS.find(tab => tab.id === tabId)!;
  const noTimesheetMetrics = TABS.find(
    item => item.id === tabId,
  )?.metrics.filter(
    metric =>
      AnalyticMetricRecord[metric].panel || AnalyticMetricRecord[metric].table,
  );
  const [viewId, setViewId] = useState<number>(1);
  const currentView = VIEWS.find(view => view.id === viewId)!;
  const [currentMetric, setCurrentMetric] = useState(currentTab.metrics[0]);
  const [range, setRange] = useState(PERIOD[0].id);
  const [comparisonRange, setComparisonRange] = useState<number | null>(null);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const getMetric = useRecoilCallback(getMetricCallback, []);
  const [metricData, setMetricData] = useState<Metric>(new MetricClass());
  const [comparisonMetricData, setComparisonMetricData] =
    useState<Metric | null>(null);
  const [metricLoader, setMetricLoader] = useState<number>(0);
  const [dateAggregation, setDateAggregation] = useState<DateAggregationEnum>(
    DateAggregationEnum.day,
  );
  const [startDate, setStartDate] = useState<Dayjs>(
    PERIOD.find(item => item.id === range)?.value.start ?? dayjs(),
  );
  const [endDate, setEndDate] = useState<Dayjs>(
    PERIOD.find(item => item.id === range)?.value.end ?? dayjs(),
  );
  const [comparisonStartDate, setComparisonStartDate] = useState<Dayjs | null>(
    null,
  );
  const [comparisonEndDate, setComparisonEndDate] = useState<Dayjs | null>(
    null,
  );

  const getMetricData = async () => {
    if (!companyId && !currentUser.companies?.length) return;
    setMetricLoader(val => val + 1);
    const res = await getMetric({
      metric: currentMetric,
      start_date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD'),
      company_id: companyId,
    });
    setMetricLoader(val => val - 1);
    if (!res || !res?.data) return;
    setMetricData(res.data);
  };

  const getComparisonMetricData = async () => {
    if (!companyId && !currentUser.companies?.length) return;
    if (!comparisonStartDate || !comparisonEndDate)
      return setComparisonMetricData(null);
    setMetricLoader(val => val + 1);
    const res = await getMetric({
      metric: currentMetric,
      start_date: comparisonStartDate.format('YYYY-MM-DD'),
      end_date: comparisonEndDate.format('YYYY-MM-DD'),
      company_id: companyId,
    });
    setMetricLoader(val => val - 1);
    if (!res || !res?.data) return;
    setComparisonMetricData(res.data);
  };

  useEffect(() => {
    if (currentView.type === viewTypeEnum.timeseries) getMetricData();
  }, [currentMetric, startDate, endDate, companyId, currentUser, currentView]);

  useEffect(() => {
    if (currentView.type === viewTypeEnum.timeseries) getComparisonMetricData();
  }, [
    currentMetric,
    comparisonStartDate,
    comparisonEndDate,
    companyId,
    currentView,
  ]);

  useEffect(() => {
    if (
      !PERIOD.find(item => item.id === range)?.isVisible.includes(
        dateAggregation,
      )
    ) {
      const newRange = PERIOD.filter(item =>
        item.isVisible.includes(dateAggregation),
      );
      if (newRange.length) setRange(newRange[0].id);
    }

    if (
      comparisonRange &&
      !COMPARISION_PERIOD.find(item => item.id === range)?.isVisible.includes(
        dateAggregation,
      )
    ) {
      const newComparisionRange = COMPARISION_PERIOD.filter(item =>
        item.isVisible.includes(dateAggregation),
      );
      if (newComparisionRange.length)
        setComparisonRange(newComparisionRange[0].id);
    }
  }, [dateAggregation]);

  useEffect(() => {
    const rangeValue = PERIOD.find(item => item.id === range)?.value;
    if (rangeValue) {
      setStartDate(rangeValue.start);
      setEndDate(rangeValue.end);
    }
  }, [range]);

  useEffect(() => {
    const rangeValue = PERIOD.find(item => item.id === range)?.value;
    if (rangeValue) {
      const comparisionRangeValue = COMPARISION_PERIOD.find(
        item => item.id === comparisonRange,
      )?.getValue(rangeValue.start, rangeValue.end);

      if (!comparisionRangeValue?.start || !comparisionRangeValue.end) {
        setComparisonEndDate(null);
        setComparisonStartDate(null);
        return;
      }

      setComparisonEndDate(comparisionRangeValue.end);
      setComparisonStartDate(comparisionRangeValue.start);
    }
  }, [comparisonRange, range]);

  useEffect(() => {
    setAppBarName('BO App');
    if (currentUser.role === UserRole.ADMIN) getAllCompanies();
    if (!currentUser.companies) {
      getUserMe();
    }
  }, []);

  return (
    <div
      style={{
        alignItems: 'start',
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
      }}
    >
      <div>
        {VIEWS.map(view => (
          <LBTButtonFilter
            key={view.id}
            isSelected={view.id === viewId}
            isGrey
            noBorderRadius
            onClick={() => {
              setViewId(view.id);
              setTabId(1);
              setCurrentMetric(TABS.find(item => item.id === 1)?.metrics[0]!);
            }}
          >
            {view.label}
          </LBTButtonFilter>
        ))}
      </div>
      <div
        style={{
          height: 'calc(100vh - 103px)',
          width: '100%',
          backgroundColor: COLORS.getInstance().BW_GREYS_SUPERLIGHT,
          display: 'flex',
          flexDirection: 'row',
          gap: '20px',
          padding: '20px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            backgroundColor: COLORS.getInstance().BW_GREYS_IPERLIGHT,
            width: '70%',
            display: 'flex',
            flexDirection: 'column',
            paddingLeft: '20px',
            paddingBottom: '20px',
          }}
        >
          <h1
            style={{
              marginBottom: '10px',
              height: '50px',
              display: 'flex',
              alignItems: 'end',
            }}
          >
            {currentTab.label}
          </h1>
          {!!metricLoader ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                width: '100%',
              }}
            >
              <LBTProgressSpinner />
            </div>
          ) : currentView.type === viewTypeEnum.timeseries ? (
            <div
              style={{
                maxHeight: '100%',
                height: '100%',
                width: '100%',
                overflowY: 'auto',
                scrollbarWidth: 'thin',
                paddingRight: '20px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Chart
                metricData={metricData}
                metricDataLabel={
                  PERIOD.find(item => item.id === range)?.label ?? ''
                }
                borderColor={COLORS.getInstance().PRIMARY_MAIN}
                backgroundColor={COLORS.getInstance().PRIMARY_SUPERLIGHT}
                dateAggregation={dateAggregation}
                comparisionMetricData={comparisonMetricData ?? undefined}
                comparisionMetricDataLabel={
                  COMPARISION_PERIOD.find(item => item.id === comparisonRange)
                    ?.label
                }
                comparisionBorderColor={COLORS.getInstance().TANGERINE_MAIN}
                comparisionBackgroundColor={
                  COLORS.getInstance().TANGERINE_SUPERLIGHT
                }
              />
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '16px',
                maxHeight: '100%',
                overflowY: 'auto',
                scrollbarWidth: 'thin',
                paddingRight: '20px',
              }}
            >
              {noTimesheetMetrics?.map(metric => {
                if (!companyId && !currentUser.companies?.length)
                  return <div />;

                if (AnalyticMetricRecord[metric].panel)
                  return (
                    <Panel
                      key={metric}
                      companies={currentUser.companies}
                      borderColor={COLORS.getInstance().PRIMARY_MAIN}
                      currentMetric={metric}
                      companyId={companyId}
                      startDate={startDate}
                      endDate={endDate}
                      comparisonStartDate={comparisonStartDate}
                      comparisonEndDate={comparisonEndDate}
                    />
                  );

                if (AnalyticMetricRecord[metric].table)
                  return (
                    <Table
                      key={metric}
                      companies={currentUser.companies}
                      borderColor={COLORS.getInstance().PRIMARY_MAIN}
                      currentMetric={metric}
                      companyId={companyId}
                      startDate={startDate}
                      endDate={endDate}
                      comparisonStartDate={comparisonStartDate}
                      comparisonEndDate={comparisonEndDate}
                    />
                  );

                return <></>;
              })}
            </div>
          )}
        </div>

        <Filter
          range={range}
          setRange={setRange}
          comparisonRange={comparisonRange}
          setComparisonRange={setComparisonRange}
          currentMetric={currentMetric}
          setCurrentMetric={setCurrentMetric}
          currentTab={currentTab}
          dateAggregation={dateAggregation}
          setDateAggregation={setDateAggregation}
          companyId={companyId}
          setCompanyId={setCompanyId}
          currentView={currentView}
          tabId={tabId}
          setTabId={setTabId}
        />
      </div>
    </div>
  );
};
